import type { Editor } from "@tiptap/vue-3"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { PropertyId } from "~/classes/entity"
import { InterfacePanelState } from "~/classes/generated/interface"
import { defaultUserInterfaceSettings } from "~/utils/flatbuffers/user"

interface PropertyDisplay {
  propertyId: PropertyId
  editable?: boolean
  multiline?: boolean
  monospace?: boolean
  inline?: boolean
}
/**
 * History items in a dictionary by file id, then by timestamp, then by property id.
 */
type PropertyHistoryDict = Record<
  string, // file id
  Record<
    string, // timestamp
    Partial<Record<PropertyId, FileProperty>>
  >
>

/**
 * We keep a separate 'state' and 'visible' property to allow for us to
 * go back to the prior open state. However, on persistence we need to
 * write this as a single "state" variable
 */
class Panel extends GlobalComposable<{
  state: InterfacePanelState
  visible: boolean
  width: number
  loading: boolean
  history: PropertyHistoryDict
  showHistoryEdits: boolean
  showHistoryMetadata: boolean
  showHistoryComments: boolean
  showHistorySharing: boolean
}> {
  commentEditor: Ref<Editor | undefined> = ref(undefined)

  override defaultState = () => ({
    state: defaultUserInterfaceSettings().panelState,
    visible: defaultUserInterfaceSettings().panelState !== InterfacePanelState.Hidden,
    width: defaultUserInterfaceSettings().panelWidth,
    loading: false,
    history: { },
    properties: { },
    showHistoryEdits: true,
    showHistoryMetadata: true,
    showHistoryComments: true,
    showHistorySharing: true,
  })

  override onCreate = () => {
    const { userInterfaceSettings } = useSelf()

    watchImmediate(userInterfaceSettings, (settings) => {
      this.state.value.visible = settings.panelState !== InterfacePanelState.Hidden
      this.state.value.state = settings.panelState
      this.state.value.width = settings.panelWidth
    })
  }

  /** Show the panel and optionally switch to the specified panel */
  showPanel = (type?: InterfacePanelState) => {
    const { isAuthenticated } = useAuthentication()
    if (!isAuthenticated() && type && [InterfacePanelState.Agent, InterfacePanelState.Chats].includes(type)) {
      console.warn("We don't currently allow anon conversations")
      return
    }

    this.state.value.visible = true
    if (type)
      this.state.value.state = type

    // TODO: width settings per panel
  }

  hidePanel = () => {
    this.state.value.visible = false
  }

  isShowingPanel = (type: InterfacePanelState): boolean => {
    return this.state.value.visible && this.state.value.state === type
  }

  togglePanel = (type?: InterfacePanelState) => {
    if (!type || type === this.state.value.state)
      this.state.value.visible ? this.hidePanel() : this.showPanel()
    else
      this.showPanel(type)
  }

  /**
   * After navigating to the agent, certain panels are not valid so we should exit from
   * them
   */
  switchPanelAfterAgentNavigation = () => {
    // close the side panel if it isn't a panel we can use in our main convo view
    if (this.isShowingPanel(InterfacePanelState.Agent)
      || this.isShowingPanel(InterfacePanelState.Waterfall)
      || this.isShowingPanel(InterfacePanelState.Activity)
    )
      this.hidePanelAndPersist()
  }

  /**
   * After navigating to a folder or search, certain panels are not valid so we should exit
   * from them
   */
  switchPanelAfterCollectionNavigation = () => {
    // close the side panel if it isn't a panel we can use in our main convo view
    if (this.isShowingPanel(InterfacePanelState.Outline))
      this.hidePanelAndPersist()
  }

  /**
   * After navigating to a file preview, certain panels are not valid so we should exit
   * from them
   */
  switchPanelAfterPreviewNavigation = () => {
    // close the side panel if it isn't a panel we can use in our main convo view
    if (this.isShowingPanel(InterfacePanelState.Waterfall)
      || this.isShowingPanel(InterfacePanelState.Outline)
    )
      this.hidePanelAndPersist()
  }

  persistPanelState = () => {
    const { persistUserInterfaceSettings } = useSelf()
    const { isAuthenticated } = useAuthentication()

    if (!isAuthenticated())
      return

    persistUserInterfaceSettings({ panelState: this.state.value.visible
      ? this.state.value.state
      : InterfacePanelState.Hidden, panelWidth: this.state.value.width })

    // TODO: width settings per panel
  }

  showPanelAndPersist = (type?: InterfacePanelState) => {
    this.showPanel(type)
    this.persistPanelState()
  }

  /**
   * TODO: Come up with a better way to construct user interface settings changes, i.e.
   * builder pattern or something.
   */
  showPanelAndPersistWithChat = (type?: InterfacePanelState) => {
    const { isAuthenticated } = useAuthentication()
    const { persistUserInterfaceSettings } = useSelf()
    const { state: conversationState } = useConversations()
    this.showPanel(type)

    if (isAuthenticated()) {
      persistUserInterfaceSettings({
        panelState: this.state.value.visible
          ? this.state.value.state
          : InterfacePanelState.Hidden,
        panelWidth: this.state.value.width,
        latestConversationId: conversationState.value.current,
      })
    }
  }

  hidePanelAndPersist = () => {
    this.hidePanel()
    this.persistPanelState()
  }

  togglePanelAndPersist = (type?: InterfacePanelState) => {
    this.togglePanel(type)
    this.persistPanelState()
  }

  focusOnCommentEditor = () => {
    if (!this.commentEditor.value)
      return this.error("Cannot focus on editor: no editor instance")
    this.commentEditor.value.commands.focus("end")
  }

  startCommenting = async () => {
    this.showPanelAndPersist(InterfacePanelState.Activity)
    this.state.value.showHistoryComments = true
    await nextTick()
    this.focusOnCommentEditor()
  }

  _targetFiles = (): Record<string, StaticFile> => {
    const { selectedFiles } = useSelection()
    const { currentRootFolder, currentFilePreview } = useNavigation()
    const { state: mainViewState } = useMainView()

    switch (mainViewState.value.state) {
      case "preview": {
        if (!currentFilePreview.value)
          return {}
        const file = currentFilePreview.value.asStatic()
        return { [file.id]: file }
      }
      case "collection": {
        const selected = selectedFiles.value
        const file = currentRootFolder.value
        if (len(selected)) {
          return selected
        }
        else if (file) {
          return { [file.id]: file.asStatic() }
        }
        else
          return {}
      }
      case "settings": {
        return {}
      }
      case "agent": {
        return {}
      }
      default: {
        throw new Error("Unknown main view state")
      }
    }
  }

  targetFiles = computed(() => this._targetFiles())

  /**
   * The "Properties" property group
   */
  readonly corePropertyDisplay = {
    [PropertyId.FILE_NAME]: {
      propertyId: PropertyId.FILE_NAME,
      editable: true,
    } as PropertyDisplay,
    [PropertyId.FILE_VIRTUAL_PATH]: {
      propertyId: PropertyId.FILE_VIRTUAL_PATH,
    } as PropertyDisplay,
    [PropertyId.FILE_SIZE]: {
      propertyId: PropertyId.FILE_SIZE,
      inline: true,
    } as PropertyDisplay,
    [PropertyId.FILE_CREATED_AT]: {
      propertyId: PropertyId.FILE_CREATED_AT,
      inline: true,
    } as PropertyDisplay,
    [PropertyId.FILE_MODIFIED_AT]: {
      propertyId: PropertyId.FILE_MODIFIED_AT,
      inline: true,
    } as PropertyDisplay,
    [PropertyId.FILE_ACCESSED_AT]: {
      propertyId: PropertyId.FILE_ACCESSED_AT,
      inline: true,
    } as PropertyDisplay,
    [PropertyId.FILE_MIME_TYPE]: {
      propertyId: PropertyId.FILE_MIME_TYPE,
      inline: true,
    } as PropertyDisplay,
    [PropertyId.FILE_VIRTUAL_SELF_ROLE]: {
      propertyId: PropertyId.FILE_VIRTUAL_SELF_ROLE,
      inline: true,
    } as PropertyDisplay,
    [PropertyId.FILE_DOWNLOAD_SOURCE_URL]: {
      propertyId: PropertyId.FILE_DOWNLOAD_SOURCE_URL,
      inline: true,
    } as PropertyDisplay,
  }

  readonly generativePropertyDisplay = {
    [PropertyId.FILE_GENERATION_PROMPT]: {
      propertyId: PropertyId.FILE_GENERATION_PROMPT,
      editable: true,
      multiline: true,
    } as PropertyDisplay,
    [PropertyId.FILE_GENERATION_NEGATIVE_PROMPT]: {
      propertyId: PropertyId.FILE_GENERATION_NEGATIVE_PROMPT,
      editable: true,
      multiline: true,
    } as PropertyDisplay,
    [PropertyId.FILE_GENERATION_SOURCE]: {
      propertyId: PropertyId.FILE_GENERATION_SOURCE,
      editable: true,
      monospace: true,
      inline: true,
    } as PropertyDisplay,
    [PropertyId.FILE_GENERATION_MODEL]: {
      propertyId: PropertyId.FILE_GENERATION_MODEL,
      editable: true,
      monospace: true,
    } as PropertyDisplay,
    [PropertyId.FILE_GENERATION_MODEL_VERSION]: {
      propertyId: PropertyId.FILE_GENERATION_MODEL_VERSION,
      editable: true,
      monospace: true,
    } as PropertyDisplay,
    [PropertyId.FILE_GENERATION_SEED]: {
      propertyId: PropertyId.FILE_GENERATION_SEED,
      editable: false,
      monospace: true,
    } as PropertyDisplay,
  }
}

const panel = new Panel("panel", theme.colors.purple.hex)
export default function () {
  return panel.use()
}
